// src/components/CustomerReportImportModal.js
// Re-usable modal for one-click customer-report imports (GFL, Prairie, WCI,
// SB Shop). Pick a file, pick mode, run a preview or apply.

import React, { useState } from "react";
import { api } from "../scripts/api";
import Modal from "./Modal";
import { UploadCloud, Play, AlertTriangle, CheckCircle2, X } from "lucide-react";

export default function CustomerReportImportModal({ kind, label, description, onClose }) {
  const [file, setFile]                   = useState(null);
  const [dry, setDry]                     = useState(true);
  const [backfillOnly, setBackfillOnly]   = useState(false);
  const [createMissing, setCreateMissing] = useState(false);
  const [busy, setBusy]                   = useState(false);
  const [err, setErr]                     = useState("");
  const [summary, setSummary]             = useState(null);

  const run = async (forceMode = null) => {
    if (!file) { setErr("Choose a file first."); return; }
    setBusy(true); setErr(""); setSummary(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const useDry = forceMode === "preview" ? true
                   : forceMode === "apply"   ? false
                   : dry;
      fd.append("dry", useDry ? "1" : "0");
      fd.append("backfill_only", backfillOnly ? "1" : "0");
      fd.append("create_missing", createMissing ? "1" : "0");

      const { data } = await api.post("/import/customer-report", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSummary(data?.summary || null);
      if (forceMode === "apply") setDry(true); // back to safe default after apply
    } catch (e) {
      setErr(e?.response?.data?.error || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>Import: {label}</h2>
          {description && <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{description}</div>}
        </div>
        <button className="btn-outline" onClick={onClose}><X size={14} /></button>
      </div>

      {/* File picker */}
      <div style={{ marginTop: 14 }}>
        <label className="muted" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
          Spreadsheet (.xlsx)
        </label>
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setSummary(null); }}
          disabled={busy}
          style={{ width: "100%" }}
        />
        {file && <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>{file.name} ({Math.round(file.size / 1024)} KB)</div>}
      </div>

      {/* Options */}
      <div style={{ marginTop: 14, display: "grid", gap: 6, padding: 10, background: "rgba(255,255,255,0.02)", border: "1px solid var(--card-border)", borderRadius: 8 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={backfillOnly} onChange={(e) => setBackfillOnly(e.target.checked)} disabled={busy} />
          <span><strong>Backfill only</strong> — only fill empty fields, never overwrite existing values</span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={createMissing} onChange={(e) => setCreateMissing(e.target.checked)} disabled={busy} />
          <span><strong>Create missing</strong> — insert new IMS records for RO/WOs that don't exist yet</span>
        </label>
      </div>

      {err && (
        <div className="card" style={{ marginTop: 12, color: "#dc2626", fontSize: 13, padding: 10, display: "flex", alignItems: "flex-start", gap: 8 }}>
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{err}</span>
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <button className="btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
        <button
          className="btn-outline"
          onClick={() => run("preview")}
          disabled={busy || !file}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <UploadCloud size={14} /> Preview
        </button>
        <button
          className="btn"
          onClick={() => {
            if (summary?.missing > 0 && !createMissing) {
              if (!window.confirm(`Apply will skip ${summary.missing} unmatched RO/WOs. Continue?`)) return;
            }
            if (!window.confirm(`Apply this import? This writes to the database.`)) return;
            run("apply");
          }}
          disabled={busy || !file}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <Play size={14} /> {busy ? "Running…" : "Apply"}
        </button>
      </div>

      {/* Summary */}
      {summary && (
        <div style={{ marginTop: 16, padding: 12, background: summary.mode === "dry-run" ? "rgba(59,130,246,0.06)" : "rgba(16,185,129,0.08)", border: `1px solid ${summary.mode === "dry-run" ? "rgba(59,130,246,0.3)" : "rgba(16,185,129,0.35)"}`, borderRadius: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <CheckCircle2 size={16} style={{ color: summary.mode === "dry-run" ? "#3b82f6" : "#16a34a" }} />
            <strong style={{ fontSize: 14 }}>
              {summary.mode === "dry-run" ? "Preview complete (no writes)" : "Import applied"}
            </strong>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, fontSize: 13 }}>
            <Stat label="Scanned"        value={summary.scanned} />
            <Stat label="Matched"        value={summary.matched} />
            <Stat label="Updated"        value={summary.updated} />
            <Stat label="Unchanged"      value={summary.unchanged} />
            <Stat label="Created"        value={summary.created} />
            <Stat label="Missing"        value={summary.missing}    warn={summary.missing > 0 && !summary.create_missing} />
            <Stat label="Fields written" value={summary.fieldsWritten} />
            <Stat label="Notes added"    value={summary.notesAppended} />
            <Stat label="Errors"         value={summary.errors}      warn={summary.errors > 0} />
          </div>

          {summary.examples?.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Examples</div>
              {summary.examples.map((ex, i) => (
                <div key={i} style={{ fontSize: 11, fontFamily: "monospace", marginBottom: 2 }}>
                  <strong>{ex.rowo}</strong> → {ex.fields.join(", ")}
                </div>
              ))}
            </div>
          )}

          {summary.missingRowos?.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ cursor: "pointer", fontSize: 12, color: "#f59e0b", fontWeight: 600 }}>
                {summary.missingRowos.length} unmatched RO/WO{summary.missingRowos.length === 1 ? "" : "s"}
              </summary>
              <div style={{ fontSize: 11, fontFamily: "monospace", marginTop: 6, color: "var(--muted)", wordBreak: "break-word" }}>
                {summary.missingRowos.slice(0, 50).join(", ")}
                {summary.missingRowos.length > 50 && ` … (+${summary.missingRowos.length - 50} more)`}
              </div>
            </details>
          )}

          {summary.createdRowos?.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ cursor: "pointer", fontSize: 12, color: "#16a34a", fontWeight: 600 }}>
                {summary.createdRowos.length} record{summary.createdRowos.length === 1 ? "" : "s"} created
              </summary>
              <div style={{ fontSize: 11, fontFamily: "monospace", marginTop: 6, wordBreak: "break-word" }}>
                {summary.createdRowos.join(", ")}
              </div>
            </details>
          )}
        </div>
      )}
    </Modal>
  );
}

function Stat({ label, value, warn }) {
  return (
    <div>
      <div className="muted" style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: warn ? "#f59e0b" : "var(--fg)" }}>{value ?? 0}</div>
    </div>
  );
}
